/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import clsx from 'clsx'

export type UiAlertVariant = 'success' | 'info' | 'warning' | 'error'

type UiAlertProps = {
    variant?: UiAlertVariant
    title?: React.ReactNode
    description?: React.ReactNode
    icon?: React.ReactNode
    dismissible?: boolean
    onDismiss?: () => void
}

const variantStyles: Record<UiAlertVariant, string> = {
    success:
        'bg-green-50 text-green-800 ring-green-600/20 dark:bg-green-900/30 dark:text-green-100 dark:ring-green-500/40',
    warning:
        'bg-amber-50 text-amber-800 ring-amber-600/20 dark:bg-amber-900/30 dark:text-amber-100 dark:ring-amber-500/40',
    error: 'bg-red-50 text-red-800 ring-red-600/20 dark:bg-red-900/30 dark:text-red-100 dark:ring-red-500/40',
    info: 'bg-blue-50 text-blue-800 ring-blue-600/20 dark:bg-blue-900/30 dark:text-blue-100 dark:ring-blue-500/40',
}

const UiAlert: React.FC<UiAlertProps> = ({
    variant = 'info',
    title,
    description,
    icon,
    dismissible,
    onDismiss,
}) => {
    return (
        <div
            className={clsx(
                'UiAlert w-full rounded-md ring-1 ring-inset px-3 py-2',
                'flex items-start gap-3',
                variantStyles[variant]
            )}
        >
            {icon && <div className={clsx('shrink-0 mt-0.5')}>{icon}</div>}
            <div className={clsx('flex-1 min-w-0')}>
                {title && (
                    <div className={clsx('text-sm font-semibold truncate')}>
                        {title}
                    </div>
                )}
                {description && (
                    <div className={clsx('text-sm leading-5 break-words')}>
                        {description}
                    </div>
                )}
            </div>
            {dismissible && (
                <button
                    type={'button'}
                    onClick={onDismiss}
                    className={clsx(
                        'ml-auto px-2 py-1 text-sm rounded border border-transparent hover:bg-black/5 dark:hover:bg-white/10'
                    )}
                >
                    ×
                </button>
            )}
        </div>
    )
}

export default UiAlert
